import React, { useEffect, useMemo, useState } from 'react';
import { Briefcase, Calendar, CircleDot, Euro, Flag, Globe, MapPin, TrendingUp, User } from 'lucide-react';
import type { Client, LeadStage } from '../../lib/types';
import { useApp } from '../AppProvider';
import { useLeadPipelineStore } from '../../lib/stores/leadPipelineStore';
import { useLeadSourceStore } from '../../lib/stores/leadSourceStore';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import {
  InlineOptionRow,
  ValueRow,
  InlineTextRow,
  UserSelectRow,
  type OptionItem,
  type UserOption,
} from '../shared/property-rows';
import { StatusManagerSkeleton } from '../ui/SkeletonLoader';
import LeadStatusUpdateDrawer from './LeadStatusUpdateDrawer';
import { useAlfaDocsStudios } from '../../lib/hooks/useAlfaDocsStudios';
import { getClientStudioIds } from '../../lib/services/alfadocsLinks';
import { StudioBadges } from '../alfadocs/StudioBadges';

interface LeadManagementRowsProps {
  lead: Client;
  leadStages: LeadStage[];
  /** Stati (client_states): distinti dalla Fase/pipeline, ognuno con description "Lead"/"Cliente". */
  clientStateOptions: OptionItem[];
  availableUsers: UserOption[];
  onLeadUpdate: (leadId: string, updates: Partial<Client>) => Promise<boolean>;
  canEdit?: boolean;
  isLead?: boolean;
  loading?: boolean;
  /** Nasconde l'header "Gestione": usato quando il chiamante fornisce già un titolo esterno (PanelSection mobile). */
  hideHeader?: boolean;
}

const rowIcon = (Icon: React.ComponentType<any>) => <Icon className="h-3 w-3" strokeWidth={1.5} />;

/**
 * Blocco "Gestione" del pannello dettagli lead/cliente a righe di proprietà
 * (sostituisce LeadStatusManager): Fase a chip, Assegnatario con pallino
 * foto/iniziali, Commerciale, Fonte, Punteggio, Valore opportunità. Stessa logica di
 * salvataggio (pipeline_stage_id vs lead_stage, last_activity_date, optimistic
 * con revert su errore).
 */
export function LeadManagementRows({
  lead,
  leadStages,
  clientStateOptions,
  availableUsers,
  onLeadUpdate,
  canEdit = true,
  isLead = true,
  loading = false,
  hideHeader = false,
}: LeadManagementRowsProps) {
  const { toast } = useApp();
  const { settings } = useSettingsStore();
  const showClientLeadStatus = settings?.menu_settings?.menu_labels?.show_client_lead_status !== false;
  const { leadSources, fetchLeadSources } = useLeadSourceStore();
  const { studios, isMultiStudio, oldestPracticeId } = useAlfaDocsStudios();
  const studioNames = useMemo(() => new Map(studios.map((s) => [s.practiceId, s.name])), [studios]);
  const practiceIds = getClientStudioIds(lead.metadata, oldestPracticeId);

  const { pipelines, stages: pipelineStages, fetchPipelines } = useLeadPipelineStore();
  // La scelta resta locale finché viene selezionata anche una fase.
  const [pendingPipelineId, setPendingPipelineId] = useState<string | null>(null);
  const needsPipeline = !lead.pipeline_id && leadStages.length === 0;
  const effectivePipelineId = lead.pipeline_id || pendingPipelineId;
  const effectiveStages: LeadStage[] = useMemo(() =>
    pendingPipelineId && !lead.pipeline_id
      ? (pipelineStages.get(pendingPipelineId) || []).map((stage) => ({
          ...stage, order: stage.sort_order,
        }))
      : leadStages,
    [pendingPipelineId, lead.pipeline_id, pipelineStages, leadStages],
  );
  const pipelineOptions = useMemo(() => pipelines
    .filter((pipeline) => pipeline.id !== 'legacy' && pipeline.is_active)
    .map((pipeline) => ({ value: pipeline.id, label: pipeline.name, color: pipeline.color })),
    [pipelines],
  );

  useEffect(() => {
    if (needsPipeline) void fetchPipelines();
  }, [needsPipeline, fetchPipelines]);

  useEffect(() => {
    setPendingPipelineId(null);
    setLostStageRequest(null);
  }, [lead.id, lead.pipeline_id]);

  // Optimistic: mostrati subito, azzerati quando arriva il dato reale o su errore
  const [optimisticStage, setOptimisticStage] = useState<string | null>(null);
  const [optimisticState, setOptimisticState] = useState<string | null | undefined>(undefined);
  const [optimisticAssignee, setOptimisticAssignee] = useState<string | null | undefined>(undefined);
  const [optimisticSource, setOptimisticSource] = useState<string | null | undefined>(undefined);
  const [optimisticSalesRep, setOptimisticSalesRep] = useState<string | null | undefined>(undefined);
  const [stageLoading, setStageLoading] = useState(false);
  const [stateLoading, setStateLoading] = useState(false);
  const [assigneeLoading, setAssigneeLoading] = useState(false);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [salesRepLoading, setSalesRepLoading] = useState(false);
  // Fase "persa" scelta da questa riga: si apre il drawer, che pretende il motivo.
  // Prima la tendina scriveva la fase direttamente e il motivo restava vuoto:
  // era l'unico punto da cui si poteva perdere un lead senza dirne il perche'
  // (riunione commerciale del 21/09/2026, 31 persi su 31 senza motivo).
  const [lostStageRequest, setLostStageRequest] = useState<string | null>(null);

  useEffect(() => {
    fetchLeadSources();
  }, [fetchLeadSources]);

  useEffect(() => {
    setOptimisticStage(null);
    setOptimisticState(undefined);
    setOptimisticAssignee(undefined);
    setOptimisticSource(undefined);
    setOptimisticSalesRep(undefined);
  }, [lead.id, lead.lead_stage, lead.pipeline_stage_id, lead.stato_cliente, lead.lead_source, lead.assigned_to, lead.sales_rep_id]);

  const currentStageId =
    optimisticStage || (lead.pipeline_id ? lead.pipeline_stage_id : pendingPipelineId ? null : lead.lead_stage) || (needsPipeline ? null : 'new');
  const currentStateId = optimisticState !== undefined ? optimisticState : lead.stato_cliente;
  const currentAssigneeId = optimisticAssignee !== undefined ? optimisticAssignee : lead.assigned_to;
  const currentSalesRepId = optimisticSalesRep !== undefined ? optimisticSalesRep : lead.sales_rep_id;
  const currentSourceId = optimisticSource !== undefined ? optimisticSource : lead.lead_source;

  const stageOptions = useMemo(
    () => effectiveStages.map((s) => ({ value: s.id, label: s.name, color: s.color })),
    [effectiveStages],
  );
  const sourceOptions = useMemo(() => {
    const configured = leadSources.map((s) => ({ value: s.id, label: s.name, description: s.description }));
    if (!currentSourceId || configured.some((s) => s.value === currentSourceId)) return configured;
    // MCP/imports also accept textual sources. A saved value is not an empty field.
    const legacy: Record<string, string> = { website: 'Sito web', sito: 'Sito web', referral: 'Passaparola', ads: 'Pubblicità', manual: 'Inserimento manuale' };
    const unavailableId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(currentSourceId);
    return [...configured, { value: currentSourceId, label: unavailableId ? 'Fonte non disponibile' : legacy[currentSourceId.toLowerCase()] || currentSourceId, description: undefined }];
  }, [leadSources, currentSourceId]);

  const handleStageSelect = async (stageId: string | null) => {
    if (!stageId || !effectiveStages.some((stage) => stage.id === stageId)) return;
    if (effectiveStages.find((s) => s.id === stageId)?.is_lost) {
      setLostStageRequest(stageId);
      return;
    }
    setOptimisticStage(stageId);
    setStageLoading(true);
    const updates: any = { last_activity_date: new Date().toISOString() };
    if (effectivePipelineId) {
      updates.pipeline_stage_id = stageId;
      if (!lead.pipeline_id) updates.pipeline_id = effectivePipelineId;
    }
    else updates.lead_stage = stageId;
    try {
      const ok = await onLeadUpdate(lead.id, updates);
      if (ok) toast.success('Fase aggiornata con successo');
      else {
        setOptimisticStage(null);
        toast.error("Errore durante l'aggiornamento della fase");
      }
    } catch {
      setOptimisticStage(null);
      toast.error("Errore durante l'aggiornamento della fase");
    } finally {
      setStageLoading(false);
    }
  };

  const handleStateSelect = async (stateId: string | null) => {
    if (!stateId) return;
    setOptimisticState(stateId);
    setStateLoading(true);
    try {
      const ok = await onLeadUpdate(lead.id, {
        stato_cliente: stateId,
        last_activity_date: new Date().toISOString(),
      } as Partial<Client>);
      if (ok) toast.success('Stato aggiornato con successo');
      else {
        setOptimisticState(undefined);
        toast.error("Errore durante l'aggiornamento dello stato");
      }
    } catch {
      setOptimisticState(undefined);
      toast.error("Errore durante l'aggiornamento dello stato");
    } finally {
      setStateLoading(false);
    }
  };

  const handleAssigneeSelect = async (userId: string) => {
    const next = userId || null;
    setOptimisticAssignee(next);
    setAssigneeLoading(true);
    try {
      const ok = await onLeadUpdate(lead.id, {
        assigned_to: next,
        last_activity_date: new Date().toISOString(),
      } as Partial<Client>);
      if (ok) toast.success('Assegnatario aggiornato');
      else {
        setOptimisticAssignee(undefined);
        toast.error("Errore durante l'assegnazione");
      }
    } catch {
      setOptimisticAssignee(undefined);
      toast.error("Errore durante l'assegnazione");
    } finally {
      setAssigneeLoading(false);
    }
  };

  // Il commerciale (sales_rep_id) e' distinto dall'assegnatario: il filtro Utente
  // li separa (Assegn./Comm.) ma prima la scheda mostrava solo l'assegnatario e il
  // commerciale si poteva cambiare solo dal drawer dei lead persi (Michele Fazi, 03/11/2026).
  const handleSalesRepSelect = async (userId: string) => {
    const next = userId || null;
    setOptimisticSalesRep(next);
    setSalesRepLoading(true);
    try {
      const ok = await onLeadUpdate(lead.id, {
        sales_rep_id: next,
        last_activity_date: new Date().toISOString(),
      } as Partial<Client>);
      if (ok) toast.success('Commerciale aggiornato');
      else {
        setOptimisticSalesRep(undefined);
        toast.error("Errore durante l'aggiornamento del commerciale");
      }
    } catch {
      setOptimisticSalesRep(undefined);
      toast.error("Errore durante l'aggiornamento del commerciale");
    } finally {
      setSalesRepLoading(false);
    }
  };

  const handleSourceSelect = async (sourceId: string | null) => {
    setOptimisticSource(sourceId);
    setSourceLoading(true);
    try {
      const ok = await onLeadUpdate(lead.id, {
        lead_source: sourceId,
        last_activity_date: new Date().toISOString(),
      } as Partial<Client>);
      if (ok) toast.success('Fonte aggiornata con successo');
      else {
        setOptimisticSource(undefined);
        toast.error("Errore durante l'aggiornamento della fonte");
      }
    } catch {
      setOptimisticSource(undefined);
      toast.error("Errore durante l'aggiornamento della fonte");
    } finally {
      setSourceLoading(false);
    }
  };

  const saveNumericField = (field: 'lead_score' | 'opportunity_value') =>
    async (raw: string | null): Promise<boolean> => {
      const parsed = raw === null ? null : field === 'lead_score' ? parseInt(raw, 10) : parseFloat(raw);
      const ok = await onLeadUpdate(lead.id, {
        [field]: parsed,
        last_activity_date: new Date().toISOString(),
      } as Partial<Client>);
      if (ok) toast.success(field === 'lead_score' ? 'Punteggio aggiornato' : 'Valore opportunità aggiornato');
      else toast.error("Errore durante l'aggiornamento");
      return ok;
    };

  if (loading) return <StatusManagerSkeleton />;

  // Riga Fase: per i clienti visibile solo se c'era già una fase/pipeline e la
  // preferenza org non la nasconde (stessa condizione dello storico LeadStatusManager).
  const showStageRow = isLead || ((lead.lead_stage || lead.pipeline_id) && showClientLeadStatus);

  return (
    <div>
      {!hideHeader && (
        <h3 className="px-1.5 pb-1 text-[11px] font-medium uppercase tracking-wide text-[rgba(55,53,47,0.5)] dark:text-gray-400">
          Gestione
        </h3>
      )}

      {lostStageRequest && (
        <LeadStatusUpdateDrawer
          isOpen
          onClose={() => setLostStageRequest(null)}
          currentStage={lostStageRequest}
          leadId={lead.id}
          currentAssignee={lead.assigned_to || undefined}
          currentSalesRepId={lead.sales_rep_id || undefined}
          leadStages={effectiveStages}
          leadPipelineId={effectivePipelineId}
          onStageUpdated={() => setLostStageRequest(null)}
          onNotesUpdated={async () => {
            // Con `requireNote` il drawer scrive nota e motivo, ma la fase la scrive
            // chi lo apre (stesso contratto del kanban).
            const stageId = lostStageRequest;
            if (!stageId) return;
            setLostStageRequest(null);
            setOptimisticStage(stageId);
            const updates: Partial<Client> = { last_activity_date: new Date().toISOString() };
            if (effectivePipelineId) {
              updates.pipeline_stage_id = stageId;
              if (!lead.pipeline_id) updates.pipeline_id = effectivePipelineId;
            }
            else updates.lead_stage = stageId;
            const ok = await onLeadUpdate(lead.id, updates);
            if (ok) toast.success('Lead segnato come perso');
            else {
              setOptimisticStage(null);
              toast.error("Errore durante l'aggiornamento della fase");
            }
          }}
          translations={{}}
          defaultTab="note"
          requireNote
          defaultStatusOpen
          priorStageId={!currentStageId || currentStageId === 'new' ? undefined : currentStageId}
        />
      )}

      {showStageRow && needsPipeline && (
        <InlineOptionRow
          icon={rowIcon(Flag)}
          label="Pipeline"
          value={pendingPipelineId}
          options={pipelineOptions}
          onSelect={(pipelineId) => {
            setPendingPipelineId(pipelineId);
            setOptimisticStage(null);
            setLostStageRequest(null);
          }}
          canEdit={canEdit && pipelineOptions.length > 0 && !stageLoading}
          valueStyle="chip"
          placeholder={pipelineOptions.length ? 'Scegli una pipeline' : 'Nessuna pipeline disponibile'}
          searchPlaceholder="Cerca pipeline..."
        />
      )}

      {showStageRow && (
        <InlineOptionRow
          icon={rowIcon(Flag)}
          // Sui clienti questa riga si chiamava "Stato" come quella sotto: due
          // righe con la stessa etichetta e valori di due mondi diversi (la
          // colonna della pipeline e lo stato del cliente). "Fase pipeline" e'
          // il nome gia' usato nel drawer di creazione e nelle impostazioni.
          label="Fase pipeline"
          value={currentStageId}
          options={stageOptions}
          onSelect={handleStageSelect}
          // Sola lettura (segnalazione a07cd87e): la fase si cambia dal drawer
          // Nota/Attivita' ("Fase lead"), dal Kanban e dal completamento task, dove si
          // lascia anche una traccia. Resta sceglibile solo per un lead ancora senza
          // pipeline, insieme alla riga "Pipeline" qui sopra (prima assegnazione).
          canEdit={canEdit && needsPipeline && stageOptions.length > 0 && !stageLoading}
          valueStyle="chip"
          loading={stageLoading}
          placeholder={needsPipeline && !pendingPipelineId ? 'Scegli prima una pipeline' : stageOptions.length === 0 ? 'Nessuna fase disponibile' : needsPipeline ? 'Scegli una fase' : isLead ? 'Nuovo' : 'Vuoto'}
          searchPlaceholder="Cerca fase..."
        />
      )}

      <InlineOptionRow
        icon={rowIcon(CircleDot)}
        label="Stato"
        value={currentStateId ?? null}
        options={clientStateOptions}
        onSelect={handleStateSelect}
        canEdit={canEdit}
        valueStyle="chip"
        loading={stateLoading}
        placeholder="Vuoto"
        searchPlaceholder="Cerca stato..."
      />

      {isMultiStudio && (
        <ValueRow icon={rowIcon(MapPin)} label="Studio AlfaDocs" wrap>
          <StudioBadges practiceIds={practiceIds} studioNames={studioNames} />
        </ValueRow>
      )}

      <UserSelectRow
        icon={rowIcon(User)}
        label="Assegnatario"
        value={currentAssigneeId || ''}
        onChange={handleAssigneeSelect}
        users={availableUsers}
        placeholder={assigneeLoading ? 'Salvataggio...' : 'Non assegnato'}
        disabled={!canEdit}
      />

      <UserSelectRow
        icon={rowIcon(Briefcase)}
        label="Commerciale"
        value={currentSalesRepId || ''}
        onChange={handleSalesRepSelect}
        users={availableUsers}
        placeholder={salesRepLoading ? 'Salvataggio...' : 'Nessuno'}
        disabled={!canEdit}
      />

      <InlineOptionRow
        icon={rowIcon(Globe)}
        label="Fonte"
        value={currentSourceId || null}
        options={sourceOptions}
        onSelect={handleSourceSelect}
        canEdit={canEdit}
        placeholder="Non specificato"
        clearLabel="Non specificato"
        loading={sourceLoading}
        searchPlaceholder="Cerca fonte..."
      />

      <InlineTextRow
        icon={rowIcon(TrendingUp)}
        label="Punteggio"
        type="number"
        value={lead.lead_score != null ? String(lead.lead_score) : null}
        onSave={saveNumericField('lead_score')}
        canEdit={canEdit}
        validate={(v) => {
          const n = parseInt(v, 10);
          return isNaN(n) || n < 0 || n > 100 ? 'Il punteggio deve essere tra 0 e 100' : null;
        }}
        display={(v) => <>{v}/100</>}
      />

      <InlineTextRow
        icon={rowIcon(Euro)}
        label="Valore opp."
        type="number"
        value={lead.opportunity_value != null ? String(lead.opportunity_value) : null}
        onSave={saveNumericField('opportunity_value')}
        canEdit={canEdit}
        validate={(v) => {
          const n = parseFloat(v);
          return isNaN(n) || n < 0 ? 'Il valore deve essere un numero positivo' : null;
        }}
        display={(v) => <>{Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}</>}
      />

      {lead.last_activity_date && (
        <div className="flex items-center gap-1.5 px-1.5 pt-1.5 text-xs text-[#787774] dark:text-gray-400">
          <Calendar className="h-3 w-3" strokeWidth={1.5} />
          <span>
            Ultima attività:{' '}
            {new Intl.DateTimeFormat('it-IT', {
              year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
            }).format(new Date(lead.last_activity_date))}
          </span>
        </div>
      )}
    </div>
  );
}
